import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { UseDataPropos } from './types';
import { useStore } from '30.quickConnect.Stores';
import { Mandatory, SynchronizeEquipmentReturnType } from '90.quickConnect.Models/enums';
import { AllFieldValueTypes, CodeReaderDesc, EntityData } from '90.quickConnect.Models/models';
import { debounceTime } from '50.quickConnect.Fields/const';
import { mapEquipmentForFormLib } from '90.quickConnect.Models/mappings';
import DataSourceValue from '30.quickConnect.Stores/helpers/DataSourceValue';
import { useQCUpdateFunctionsContext } from '20.formLib/DeclarationContainer/contexts';

const useData = (
  field: CodeReaderDesc,
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
  setSelectedIndex: React.Dispatch<React.SetStateAction<string | undefined>>,
  updateFieldErrors: (mandatoryFieldFullPathId: string, newErrors: string[]) => void,
): UseDataPropos => {
  const { searchEquipment, mustBeOfSchema, mustHaveQCTag, fullPathId, value, mandatory } = field;

  const { t } = useTranslation(['axios', 'declaration']);
  const {
    EquipmentsStore: { synchronizeEquipmentsWithLockAsync },
    DeclarationStore: { registerNewDataSource },
  } = useStore();

  const { updateProperty } = useQCUpdateFunctionsContext();

  const [code, setCode] = useState<string>('');
  const [localValue, setLocalValue] = useState<string | undefined>((value?.toString() as string) ?? undefined);
  const [open, setOpen] = useState(false);
  const clickLockedRef = useRef(false);
  const handleClickOpen = useCallback(() => {
    clickLockedRef.current = false;
    setOpen(true);
    setSelectedIndex(fullPathId);
  }, [fullPathId, setSelectedIndex]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const updateGlobalState = useCallback(() => {
    updateDeclaration(fullPathId, code);
  }, [updateDeclaration, fullPathId, code]);

  const debouncedUpdateGlobalState = useDebouncedCallback(() => {
    updateGlobalState();
  }, debounceTime);

  const handleAddRfidCode = useCallback(() => {
    if (clickLockedRef.current) return;
    clickLockedRef.current = true;
    setLocalValue(code);
    updateGlobalState();
    setCode('');
    handleClose();
  }, [code, handleClose, updateGlobalState]);

  const updateNewEntity = useCallback(
    (newData: EntityData | undefined, localCode: string) => {
      if (newData) {
        // On met a jour la prop 'entityData'
        updateProperty(fullPathId, 'entityData', newData);
        // Enregistrement de l'entityData pour les internalData
        const dataSourceValue: DataSourceValue = mapEquipmentForFormLib(newData);
        registerNewDataSource(fullPathId, dataSourceValue);

        if (mandatory !== Mandatory.Required) {
          setLocalValue(localCode);
          updateFieldErrors(fullPathId, []);
          debouncedUpdateGlobalState();
          handleClose();
        } else {
          setLocalValue(localCode);
          debouncedUpdateGlobalState();
          handleClose();
        }
      } else {
        if (mandatory !== Mandatory.Required) {
          updateFieldErrors(fullPathId, [t('errors_mandatory_field', { ns: 'declaration' })]);
          setLocalValue(localCode);
          debouncedUpdateGlobalState();
          handleClose();
        } else {
          setCode('');
          debouncedUpdateGlobalState();
          handleClose();
        }
      }
    },
    [
      debouncedUpdateGlobalState,
      fullPathId,
      handleClose,
      mandatory,
      registerNewDataSource,
      t,
      updateFieldErrors,
      updateProperty,
    ],
  );

  const checkEquipment = useCallback((): void => {
    if (clickLockedRef.current) return;
    clickLockedRef.current = true;
    if (code && searchEquipment) {
      //Ajout du tag "##" au code d'équipement pour le différentier côté API du QRcode
      synchronizeEquipmentsWithLockAsync(
        `##${code}`,
        mustBeOfSchema,
        mustHaveQCTag,
        SynchronizeEquipmentReturnType.All,
        undefined,
        t,
      ).then((entity) => updateNewEntity(entity, code));
    } else {
      setLocalValue(code);
      handleClose();
      debouncedUpdateGlobalState();
    }
  }, [
    code,
    searchEquipment,
    synchronizeEquipmentsWithLockAsync,
    mustBeOfSchema,
    mustHaveQCTag,
    t,
    updateNewEntity,
    handleClose,
    debouncedUpdateGlobalState,
  ]);
  const handleClear = useCallback(() => {
    setLocalValue('');
    setCode('');
    updateProperty(fullPathId, 'entityData', undefined);
    updateFieldErrors(fullPathId, []);
    debouncedUpdateGlobalState();
  }, [debouncedUpdateGlobalState, fullPathId, updateFieldErrors, updateProperty]);

  // useEffect pour les références...
  useEffect(() => {
    const newLocalValue = typeof value === 'string' ? value : undefined;
    setLocalValue((previousLocalState: string | undefined) =>
      newLocalValue !== previousLocalState ? newLocalValue : previousLocalState,
    );
  }, [value]);

  return {
    code,
    setCode,
    checkEquipment,
    localValue,
    setLocalValue,
    handleClickOpen,
    handleClose,
    open,
    handleClear,
    handleAddRfidCode,
    updateNewEntity,
  };
};
export default useData;
